import { Component } from '@angular/core';
import { SearchWordService } from './search-word.service';
import { DictionaryResultType } from './types/dictionary-result.type';
import { Pronunciation } from './types/pronunciation.type';

@Component({
  selector: 'lp2-dictionary',
  templateUrl: './dictionary.component.html',
  styleUrls: ['./dictionary.component.scss'],
})
export class DictionaryComponent {
  title = 'Dictionary';
  searchWord: string;
  lastSearchedWord: string;
  searchResults: DictionaryResultType[];
  translationResult: any;
  definitionNotFound = false;
  _translationLoading = false;
  translationNotFound = false;
  isTranslationAvailable = false;
  suggestions: [any];
  maxSearchAttempts = 2;
  pronunciations = [];

  private _loading = false;

  constructor(private searchWordService: SearchWordService) {
    this.searchWord = '';
    this.isTranslationAvailable = this.searchWordService.isTranslationAvailable();
  }

  get loading(): boolean {
    return this._loading;
  }

  get translationLoading(): boolean {
    return this._translationLoading;
  }

  startLoading(): void {
    this._loading = true;
  }

  stopLoading(): void {
    this._loading = false;
  }

  startTranslationLoading(): void {
    this._translationLoading = true;
  }

  stopTranslationLoading(): void {
    this._translationLoading = false;
  }

  playPronunciation(audioUrl: string) {
    if (!audioUrl) {
      return;
    }
    const audio = new Audio(audioUrl);
    audio.play();
  }

  getPronunciations(results: DictionaryResultType[]) {
    return results.map((result) => {
      const pronunciationsObj = {};

      if (!result.lexicalEntries) {
        return [];
      }
      result.lexicalEntries.forEach((lexicalEntry) => {
        if (!lexicalEntry.entries) {
          return [];
        }
        lexicalEntry.entries.forEach((entry) => {
          if (!entry.pronunciations) {
            return [];
          }
          entry.pronunciations.forEach((pronunciation) => {
            if (pronunciation.audioFile && pronunciation.phoneticSpelling) {
              pronunciationsObj[pronunciation.phoneticSpelling] = pronunciation;
            }
          });
        });
      });

      return Object.keys(pronunciationsObj).map((key) => pronunciationsObj[key]);
    });
  }

  searchByWord(word) {
    this.searchWord = word;
    this.search();
  }

  resetSearch() {
    this.searchResults = null;
    this.pronunciations = null;
    this.translationResult = null;
    this.definitionNotFound = false;
    this.translationNotFound = false;
    this.suggestions = null;
  }

  onTabChange(event) {
    if (event.nextId === 'translation-tab') {
      if (!this.translationResult && !this.translationLoading) {
        this.searchTranslation();
      }
    }
  }

  searchTranslation() {
    if (!this.searchWord || this.searchResults === null || this.translationNotFound) {
      return;
    }

    this._translationLoading = true;
    this.searchWordService.searchTranslation(this.searchWord).subscribe(
      (result: any) => {
        this._translationLoading = false;
        if (result.results.length) {
          this.translationResult = result.results[0];
        } else {
          this.translationNotFound = true;
        }
      },
      (error) => {
        this._translationLoading = false;
        const status = error.status;
        this.translationNotFound = true;
      }
    );
  }

  markDefinitionNotFound() {
    this.definitionNotFound = true;
    this.lastSearchedWord = this.searchWord;
  }

  searchMatch(attempt?: number): void {
    this.searchWordService.searchMatch(this.searchWord).subscribe(
      (result: any) => {
        if (!result) {
          this.stopLoading();
          this.markDefinitionNotFound();
          return;
        }
        const { results } = result;
        if (results && results.length) {
          const bestMatch: any = results[0];
          switch (bestMatch.matchType) {
            case 'variantForm':
              this.searchWord = bestMatch.matchForm;
              this.search(attempt + 1);
              break;
            case 'inflection':
              this.searchWord = bestMatch.word;
              this.search(attempt + 1);
              break;
            default:
              this.stopLoading();
              this.markDefinitionNotFound();
              this.suggestions = results;
          }
        } else {
          this.stopLoading();
          this.markDefinitionNotFound();
        }
      },
      (error) => {
        const { status } = error;
        if (status === 404) {
          this.markDefinitionNotFound();
        } else {
          this.resetSearch();
          console.error(error);
        }
      }
    );
  }

  search(attempt: number = 1): void {
    if (!this.searchWord || this.searchWord === this.lastSearchedWord) {
      return;
    }
    if (attempt > this.maxSearchAttempts) {
      this.markDefinitionNotFound();
      this.lastSearchedWord = this.searchWord;
      this.stopLoading();
      return;
    }

    this.resetSearch();
    this.startLoading();
    this.searchWordService.searchWord(this.searchWord).subscribe(
      (result) => {
        const { results } = result;
        if (results && results.length) {
          this.searchResults = result.results;
          this.pronunciations = this.getPronunciations(result.results);
          this.lastSearchedWord = this.searchWord;
          this.stopLoading();
        } else {
          this.searchMatch(attempt);
        }
      },
      (error) => {
        const status = error.status;
        if (status === 404) {
          this.searchMatch(attempt);
        } else {
          this.stopLoading();
          this.resetSearch();
          console.error(error);
        }
      }
    );
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/dictionary/dictionary.component.ts